import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, Loader2, LogOut, Mail, Phone, User as UserIcon, IdCard, Save, Trash2, Building2, KeyRound, Lock, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { RoleBadge, VerifiedBadge } from "@/components/badges";
import { VerifyIdentity } from "@/components/VerifyIdentity";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

function initials(n = "") {
  return n.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const fileRef = useRef(null);
  const [companies, setCompanies] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [changingPwd, setChangingPwd] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
  }, [user]);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
  }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Navn kan ikke være tomt");
    setSaving(true);
    try {
      await api.put("/auth/me", form);
      await refreshUser();
      toast.success("Profil oppdatert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Velg en bildefil");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/auth/me/avatar", fd);
      await refreshUser();
      toast.success("Profilbilde oppdatert");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    try {
      await api.delete("/auth/me/avatar");
      await refreshUser();
      toast.success("Profilbilde fjernet");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const changePassword = async () => {
    if (!pwd.current) return toast.error("Skriv inn nåværende passord");
    if (pwd.next.length < 8) return toast.error("Nytt passord må ha minst 8 tegn");
    if (pwd.next !== pwd.confirm) return toast.error("De nye passordene er ikke like");
    setChangingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Passordet er endret");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setChangingPwd(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="profile-page">
      <header className="sticky top-0 z-40 flex h-16 items-center border-b border-border/60 bg-background/80 px-5 backdrop-blur-xl sm:px-8">
        <Logo subtitle="Profil" />
        <div className="ml-auto flex items-center gap-1.5">
          <ThemeToggle />
          <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground" data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}>
            <LogOut className="h-4 w-4" /> Logg ut
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8 sm:px-8 lg:py-12">
        <button onClick={() => navigate("/hjem")} className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="back-dashboard">
          <ArrowLeft className="h-4 w-4" /> Til selskaper
        </button>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="relative overflow-hidden rounded-2xl border border-border/60 bg-primary text-primary-foreground shadow-sm">
          <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-lime/25 blur-2xl" />
          <div className="relative flex flex-col items-center gap-4 p-6 sm:flex-row sm:items-end sm:p-8">
            <div className="relative shrink-0">
              <Avatar className="h-24 w-24 ring-4 ring-lime/60">
                <AvatarImage src={user?.avatar || undefined} />
                <AvatarFallback className="bg-background/10 text-2xl font-bold text-primary-foreground">{initials(user?.name)}</AvatarFallback>
              </Avatar>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="profile-upload-avatar-btn"
                className="absolute -bottom-1 -right-1 grid h-9 w-9 place-items-center rounded-full bg-lime text-lime-foreground shadow-md transition-transform hover:scale-105 active:scale-95 disabled:opacity-60">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-avatar-input" />
            </div>
            <div className="min-w-0 text-center sm:pb-1 sm:text-left">
              <h1 className="font-headings text-2xl font-extrabold tracking-tight">{user?.name || "…"}</h1>
              <p className="text-sm text-primary-foreground/70">{user?.email}</p>
              {user?.avatar ? (
                <button onClick={removeAvatar} data-testid="profile-remove-avatar-btn" className="mt-2 inline-flex items-center gap-1 text-xs text-primary-foreground/60 underline-offset-2 transition-colors hover:text-primary-foreground hover:underline">
                  <Trash2 className="h-3 w-3" /> Fjern bilde
                </button>
              ) : (
                <p className="mt-2 text-xs text-primary-foreground/60">Trykk kameraet for å laste opp et profilbilde</p>
              )}
            </div>
          </div>
        </motion.div>

        <div className="mt-6 rounded-2xl border border-border/60 bg-card p-6 shadow-sm" data-testid="identity-status-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <h2 className="flex items-center gap-2 font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
                <ShieldCheck className="h-4 w-4" /> Identitet
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {user?.identity_verified
                  ? "Identiteten din er bekreftet med BankID."
                  : user?.verification_status === "pending"
                  ? "Verifiseringen venter på godkjenning fra admin."
                  : "Verifiser fødselsnummeret ditt med BankID for å låse opp alle funksjoner."}
              </p>
            </div>
            {user?.identity_verified ? (
              <VerifiedBadge testId="profile-verified-badge" className="px-3 py-1 text-sm" />
            ) : user?.verification_status === "pending" ? (
              <span data-testid="profile-verify-pending" className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                <Clock className="h-3.5 w-3.5" /> Venter på godkjenning
              </span>
            ) : (
              <Button onClick={() => setVerifyOpen(true)} className="gap-2 active:scale-[0.98]" data-testid="profile-verify-nid-btn">
                <ShieldCheck className="h-4 w-4" /> Verifiser fødselsnummer
              </Button>
            )}
          </div>
          {!user?.identity_verified ? (
            <VerifyIdentity user={user} onDone={refreshUser} showBanner={false} open={verifyOpen} onOpenChange={setVerifyOpen} />
          ) : null}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-5">
          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm lg:col-span-3">
            <h2 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Personopplysninger</h2>
            <div className="mt-5 space-y-4">
              <Field icon={UserIcon} label="Navn"><Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field icon={Mail} label="E-post"><Input data-testid="profile-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field icon={Phone} label="Telefon"><Input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+47 …" /></Field>
              <Field icon={IdCard} label="Fødselsnummer (kan ikke endres)"><Input value={user?.national_id || ""} disabled className="opacity-70" /></Field>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={save} disabled={saving} className="gap-2 active:scale-[0.98]" data-testid="profile-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre endringer
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm lg:col-span-2">
            <h2 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Dine roller</h2>
            <div className="mt-4 space-y-3">
              {companies === null ? (
                [1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-lg" />)
              ) : companies.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen selskaper ennå.</p>
              ) : (
                companies.map((c) => (
                  <button key={c.id} onClick={() => navigate(`/selskap/${c.id}`)} data-testid={`profile-company-${c.id}`}
                    className="flex w-full items-center gap-3 rounded-lg border border-border/60 p-3 text-left transition-colors hover:bg-muted">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Building2 className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{c.name}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">{(c.my_roles?.length ? c.my_roles : [c.my_role]).map((r) => <RoleBadge key={r} role={r} />)}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-border/60 bg-card p-6 shadow-sm" data-testid="security-card">
          <h2 className="flex items-center gap-2 font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
            <Lock className="h-4 w-4" /> Sikkerhet · Bytt passord
          </h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            <Field icon={KeyRound} label="Nåværende passord">
              <Input data-testid="change-current-password-input" type="password" autoComplete="current-password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} placeholder="••••••••" />
            </Field>
            <Field icon={KeyRound} label="Nytt passord">
              <Input data-testid="change-new-password-input" type="password" autoComplete="new-password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} placeholder="min. 8 tegn" />
            </Field>
            <Field icon={KeyRound} label="Bekreft nytt passord">
              <Input data-testid="change-confirm-password-input" type="password" autoComplete="new-password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} placeholder="gjenta" />
            </Field>
          </div>
          {user?.is_admin ? (
            <p className="mt-4 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
              Du er plattform-admin. Passordet du setter her lagres og beholdes – det overstyres ikke lenger av miljøvariabelen ved omstart.
            </p>
          ) : null}
          <div className="mt-5 flex justify-end">
            <Button onClick={changePassword} disabled={changingPwd} className="gap-2 active:scale-[0.98]" data-testid="change-password-btn">
              {changingPwd ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Endre passord
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
